import { Box, Divider, Grid, TextField } from '@mui/material';
import { ApiResponse } from 'apisauce';
import { ValidationErrors } from 'final-form';
import * as R from 'ramda';
import { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { makeStyles } from 'tss-react/mui';
import * as yup from 'yup';
import LangEnum from '../../../../lang/LangEnum';
import yupValidation from '../../../../lib/yupValidation';
import snackbarStore from '../../../../stores/Snackbar.store';
import teceoInputConfig from '../../../../utils/TeceoInputConfig';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { useChatContext } from '../../contexts/chat/chat.context';
import {
  Chat,
  ChatParticipant,
  ChatRoomFormValues,
  ChatRoomInitialValues,
  ChatType,
  CreateChatDto,
  CreateChatParticipant,
  CreateChatParticipants,
  UpdateChatData,
} from '../../types/chat.types';
import chatRepository from '../../utils/chat.repository';
import { formatInitialParticipants } from '../../utils/chatParticipants.utils';
import {
  CHAT_CONFIG_VIEW_CONTENT_HEIGHT,
  CHAT_FOOTER_HEIGHT,
} from '../ChatConfigViews/components/ChatContainerContent/utils/styles.const';
import { CHAT_HEADER_MAX_HEIGHT } from '../ChatHeaderTemplate/ChatHeaderTemplate';
import ChatFormFooter from '../form/ChatFormFooter/ChatFormFooter';
import ChatParticipantListField from '../form/ChatParticipantListField';

const useStyles = makeStyles()((theme) => ({
  root: {
    marginTop: '8px',
    height: `calc(${CHAT_CONFIG_VIEW_CONTENT_HEIGHT} + 100px)`,

    [theme.breakpoints.down('sm')]: {
      height: `calc(105dvh - ${CHAT_HEADER_MAX_HEIGHT} - ${CHAT_FOOTER_HEIGHT})`,
    },
  },
  divider: {
    height: 2,
    backgroundColor: theme.palette.primary.main,
  },
}));

type ChatRoomFormProps = {
  chatId?: string;
  onGoBack?: () => void;
  onSuccess?: () => void;
  initialValues?: ChatRoomInitialValues;
};

const participantsValidate = yup.object().shape({
  participants: yup.array().required(),
  title: yup.string().trim().required(),
});

const validate = async (values: Omit<ChatRoomFormValues, 'title'>): Promise<ValidationErrors> =>
  yupValidation(participantsValidate)({ ...values });

const ChatRoomForm = ({ initialValues, chatId, onGoBack, onSuccess }: ChatRoomFormProps) => {
  const { classes } = useStyles();

  const { translate } = useTranslation();
  const { onChangeSelectedChat, updateChatWithNewInfo } = useChatContext();
  const { team } = useApplicationContext();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDataToSubmit = (values: ChatRoomFormValues): CreateChatDto => {
    const stores = values.participants.reduce((currentStores: CreateChatParticipant[], current) => {
      if (current.teamType === 'brand') {
        return currentStores;
      }

      const participant: CreateChatParticipant = {
        id: current.id,
      };
      return [...currentStores, participant];
    }, []);

    const participants: CreateChatParticipants = {
      brand: {
        id: team?.brand?.id as string,
      },
      stores,
    };

    return {
      title: values.title,
      participants,
      type: ChatType.GROUP,
    };
  };

  const handleSubmitCreateChat = async (values: ChatRoomFormValues) => {
    const data: CreateChatDto = formatDataToSubmit(values);

    setIsSubmitting(true);
    let response: ApiResponse<Chat>;

    if (initialValues && chatId) {
      const updateData: UpdateChatData = { participants: data.participants, title: data.title };
      response = await chatRepository.updateChat(updateData, chatId);
    } else {
      response = await chatRepository.createChat(data);
    }

    setIsSubmitting(false);

    if (!response.ok || !response.data) {
      snackbarStore.handleMessage({
        type: 'error',
        message: translate(LangEnum.SNACKBAR_ERROR_CREATING_CHAT),
      });

      return;
    }

    const updatedChat = response.data;
    if (!chatId) {
      onChangeSelectedChat(updatedChat.id);
    } else {
      updateChatWithNewInfo(updatedChat);
    }

    onSuccess?.();
  };

  const participants = formatInitialParticipants(initialValues?.participants);

  return (
    <Form
      validate={validate}
      onSubmit={handleSubmitCreateChat}
      initialValues={{
        ...initialValues,
        participants,
      }}
      render={({ handleSubmit, values }) => {
        const submitButtonDisabled =
          R.isEmpty(values.participants) ||
          R.isNil(values.participants) ||
          R.isEmpty(values.title) ||
          R.isNil(values.title);

        return (
          <form className={classes.root} onSubmit={handleSubmit}>
            <Grid container direction="column" wrap="nowrap" className={classes.root}>
              <Grid>
                <Box
                  sx={{
                    paddingTop: 1,
                    paddingX: 2,
                  }}
                >
                  <Field name="title">
                    {({ input, meta }) => (
                      <TextField
                        autoFocus
                        type="text"
                        error={meta.touched && !!meta.error}
                        label={translate(LangEnum.CHAT_ROOM_GROUP_FORM_TITLE)}
                        {...teceoInputConfig}
                        {...input}
                        slotProps={{
                          input: {
                            style: { borderRadius: '30px' },
                          },
                        }}
                      />
                    )}
                  </Field>
                </Box>
              </Grid>

              <Grid size="grow">
                <Field name="participants">
                  {({ input: { value, onChange } }) => (
                    <ChatParticipantListField
                      value={value}
                      onRemoveParticipant={(participant) =>
                        onChange(value.filter(({ id }: ChatParticipant) => id !== participant.id))
                      }
                      onAddParticipant={(participant) => onChange([...value, participant])}
                    />
                  )}
                </Field>
              </Grid>

              <Grid>
                <Box
                  sx={{
                    height: CHAT_FOOTER_HEIGHT,
                  }}
                >
                  <Divider />

                  <ChatFormFooter
                    type="submit"
                    onGoBack={onGoBack}
                    isSubmitting={isSubmitting}
                    submitButtonIsDisabled={submitButtonDisabled}
                    submitButtonLabel={
                      initialValues?.participants
                        ? LangEnum.CHAT_ROOM_GROUP_FORM_EDIT_BUTTON
                        : LangEnum.CHAT_ROOM_GROUP_FORM_CREATE_BUTTON
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </form>
        );
      }}
    />
  );
};

export default ChatRoomForm;
